import Dexie from 'dexie'

export interface TranslationTextCache {
  id?: number
  cacheKey: string
  // originalText: string
  translatedText: string
  // from: string
  // to: string
  // model: string
  timestamp: number
}

export interface TranslationImgCache {
  id?: number
  cacheKey: string
  imgData: string
  timestamp: number
}

class TranslationCacheDB extends Dexie {
  textCache!: Dexie.Table<TranslationTextCache, number>
  imgCache!: Dexie.Table<TranslationImgCache, number>
  glossaryCache!: Dexie.Table<ServerDataTypes.GlossaryItem, number>

  constructor() {
    super('TranslationCacheDB')
    this.version(2).stores({
      // 复合索引提高查询性能
      textCache: '++id, cacheKey, timestamp',
      imgCache: '++id, cacheKey, timestamp',
      glossaryCache: '++id, [fromLang+toLang], fromText, toText,termId',
    })
  }

  async batchSetGlossaryData(glossaryDataArray: ServerDataTypes.GlossaryItem[]) {
    await this.glossaryCache.clear()
    return await this.glossaryCache.bulkAdd(glossaryDataArray)
  }

  // 批量查询方法
  async batchGetGlossaryData({
    fromLang,
    toLang,
  }: {
    fromLang: string
    toLang: string
  }): Promise<ServerDataTypes.GlossaryItem[]> {
    return await this.glossaryCache
      .where(['fromLang', 'toLang'])
      .equals([fromLang, toLang])
      .toArray()
  }

  async batchSetText(translations: TranslationTextCache[]) {
    // 在批量插入前检查存储空间
    const isFull = await this.checkStorageIsFull()
    if (isFull) {
      await this.smartCleanup()
    }
    return await this.textCache.bulkAdd(translations)
  }

  async batchSetImg(translations: TranslationImgCache[]) {
    // 在批量插入前检查存储空间
    const isFull = await this.checkStorageIsFull()
    if (isFull) {
      await this.smartCleanup()
    }
    return await this.imgCache.bulkAdd(translations)
  }

  async getTextByKey(key: string) {
    return await this.textCache.where('cacheKey').equals(key).first()
  }

  async getImgByKey(key: string) {
    return await this.imgCache.where('cacheKey').equals(key).first()
  }

  // 批量查询方法
  async batchGetTextByKeys(keys: string[]): Promise<TranslationTextCache[]> {
    return await this.textCache.where('cacheKey').anyOf(keys).toArray()
  }

  async batchGetImgByKeys(keys: string[]): Promise<TranslationImgCache[]> {
    return await this.imgCache.where('cacheKey').anyOf(keys).toArray()
  }

  // 清理旧缓存
  async cleanOldCache(type: 'text' | 'img') {
    console.log(`开始清理 ${type} 缓存...`)

    try {
      const table = type === 'text' ? this.textCache : this.imgCache
      const currentTimestamp = Date.now()

      // 策略1: 按时间删除 - 删除30天前的数据
      const oneMonthAgo = currentTimestamp - 30 * 24 * 60 * 60 * 1000
      const deletedByTime = await table.where('timestamp').below(oneMonthAgo).delete()
      console.log(`按时间删除了 ${deletedByTime} 条 ${type} 记录`)

      // 检查清理后是否还需要进一步清理
      const remainingCount = await table.count()
      const maxRecords = type === 'img' ? 100 : 5000 // 图片缓存保留更少记录

      // 策略2: 按数量限制 - 如果记录数还是太多，保留最新的记录
      if (remainingCount > maxRecords) {
        const recordsToDelete = remainingCount - Math.floor(maxRecords * 0.8) // 删除到80%的限制

        // 获取最旧的记录ID
        const oldRecords = await table.orderBy('timestamp').limit(recordsToDelete).primaryKeys()

        const deletedByCount = await table.bulkDelete(oldRecords)
        console.log(`按数量限制删除了 ${deletedByCount} 条 ${type} 记录`)
      }

      // 策略3: 智能清理 - 检查存储空间，如果还是不够，优先删除图片缓存
      const storageStillFull = await this.checkStorageIsFull()
      if (storageStillFull && type === 'img') {
        // 如果存储空间还是满的，对图片缓存进行更激进的清理
        await this.aggressiveCleanImgCache()
      }

      const finalCount = await table.count()
      console.log(`${type} 缓存清理完成，剩余记录数: ${finalCount}`)
    } catch (error) {
      console.error(`清理 ${type} 缓存时出错:`, error)
    }
  }

  // 激进的图片缓存清理策略
  private async aggressiveCleanImgCache() {
    try {
      const currentCount = await this.imgCache.count()
      const targetCount = 50 // 只保留50条最新的图片缓存

      if (currentCount > targetCount) {
        const recordsToDelete = currentCount - targetCount
        const oldRecords = await this.imgCache
          .orderBy('timestamp')
          .limit(recordsToDelete)
          .primaryKeys()

        const deleted = await this.imgCache.bulkDelete(oldRecords)
        console.log(`激进清理删除了 ${deleted} 条图片缓存`)
      }
    } catch (error) {
      console.error('激进清理图片缓存时出错:', error)
    }
  }

  // 改进的存储空间检查，提供更详细的信息
  async checkStorageIsFull(threshold: number = 0.8) {
    try {
      const estimate = await navigator.storage.estimate()
      const used = estimate.usage || 0
      const quota = estimate.quota || 0
      const usageRatio = used / quota

      console.log(
        `存储使用情况: ${Math.round(usageRatio * 100)}% (${Math.round(
          used / 1024 / 1024
        )}MB / ${Math.round(quota / 1024 / 1024)}MB)`
      )

      return usageRatio > threshold
    } catch (error) {
      console.warn('无法检查存储空间，跳过清理:', error)
      return false
    }
  }

  // 获取存储使用情况
  async getStorageInfo() {
    try {
      const estimate = await navigator.storage.estimate()
      const textCacheCount = await this.textCache.count()
      const imgCacheCount = await this.imgCache.count()

      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0,
        usagePercentage: ((estimate.usage || 0) / (estimate.quota || 1)) * 100,
        textCacheCount,
        imgCacheCount,
        usedMB: Math.round(((estimate.usage || 0) / 1024 / 1024) * 100) / 100,
        quotaMB: Math.round(((estimate.quota || 0) / 1024 / 1024) * 100) / 100,
      }
    } catch (error) {
      console.warn('无法获取存储信息:', error)
      return null
    }
  }

  // 新增：获取各表的空间占用估算
  async getTableSizeEstimate() {
    try {
      const textCount = await this.textCache.count()
      const imgCount = await this.imgCache.count()

      // 粗略估算每条记录的大小
      const avgTextSize = 200 // 字节 (估算)
      const avgImgSize = 50000 // 字节 (base64图片通常较大)

      const estimatedTextSize = textCount * avgTextSize
      const estimatedImgSize = imgCount * avgImgSize

      return {
        textCache: {
          count: textCount,
          estimatedSizeMB: Math.round((estimatedTextSize / 1024 / 1024) * 100) / 100,
        },
        imgCache: {
          count: imgCount,
          estimatedSizeMB: Math.round((estimatedImgSize / 1024 / 1024) * 100) / 100,
        },
        totalEstimatedMB:
          Math.round(((estimatedTextSize + estimatedImgSize) / 1024 / 1024) * 100) / 100,
      }
    } catch (error) {
      console.error('获取表大小估算时出错:', error)
      return null
    }
  }

  // 新增：智能清理策略 - 根据存储压力自动选择清理策略
  async smartCleanup() {
    const storageInfo = await this.getStorageInfo()
    const tableSize = await this.getTableSizeEstimate()

    if (!storageInfo || !tableSize) {
      console.warn('无法获取存储信息，执行基础清理')
      await this.cleanOldCache('img')
      await this.cleanOldCache('text')
      return
    }

    console.log('存储使用情况:', storageInfo)
    console.log('表大小估算:', tableSize)

    // 根据使用率决定清理策略
    if (storageInfo.usagePercentage > 90) {
      console.log('存储空间严重不足，执行激进清理')
      await this.aggressiveCleanImgCache()
      await this.cleanOldCache('text')
    } else if (storageInfo.usagePercentage > 80) {
      console.log('存储空间紧张，优先清理图片缓存')
      await this.cleanOldCache('img')
      if (await this.checkStorageIsFull()) {
        await this.cleanOldCache('text')
      }
    } else if (storageInfo.usagePercentage > 70) {
      console.log('存储空间使用较多，清理旧数据')
      // 优先清理图片，因为占用空间更大
      if (tableSize.imgCache.estimatedSizeMB > tableSize.textCache.estimatedSizeMB) {
        await this.cleanOldCache('img')
      } else {
        await this.cleanOldCache('text')
      }
    }
  }
}

export const indexDbHelper = new TranslationCacheDB()
